import { cloneDeep, isEqual } from 'lodash';
import React, { useEffect } from 'react';
import { EStringOp, IEventRule } from '@src/emp-3.0/types';

import styles from './index.less';
import { getEmptyRuleGroupInAPIForm } from '@src/emp-3.0/utils';
import EventBox from '@src/emp-3.0/components/MappedEventBuilder/MappingRulesBuilder/EventBox';
import Icon from '@ant-design/icons';
import PlusIcon from '@icons/emp/plus_icon.svg';
import { eventTypeOfMappedEvent } from '@src/common/constant/enum';
export interface IMappingRulesBuilderProps {
  rules: IEventRule[][] | null;
  onChange?: (rules: IEventRule[][]) => void;
  highlightEvents?: number[];
  validation?: {
    failed: boolean;
    fields: string[];
    message?: string;
    errorIndex?: Record<number, number[]>;
  };
  disableAddRawEvent?: boolean;
  src?: string;
}

const MappingRulesBuilder = ({
  rules,
  onChange,
  highlightEvents,
  validation,
  disableAddRawEvent = false,
  ...rest
}: IMappingRulesBuilderProps) => {
  const [activeIndex, setActiveIndex] = React.useState<number>(0);
  const [highlights, setHighlights] = React.useState<number[]>(highlightEvents || [0]);
  const [eventRules, setEventRules] = React.useState<IEventRule[][] | null>(null);

  useEffect(() => {
    if (!isEqual(eventRules, rules) && eventRules) {
      onChange?.(eventRules);
    }
  }, [eventRules]);

  useEffect(() => {
    setEventRules(cloneDeep(rules));
  }, [rules]);

  // autofocus the last one.
  useEffect(() => {
    if (!eventRules) return;

    if (!activeIndex) setActiveIndex(0);
  }, [eventRules, activeIndex]);

  useEffect(() => {
    // by requirements, may highlights multiple events one time.
    if (highlightEvents) {
      setHighlights([...highlightEvents]);
    } else {
      setHighlights([0]);
    }
  }, [highlightEvents]);

  useEffect(() => {
    if (activeIndex !== -1) {
      setHighlights([activeIndex]);
    } else {
      setHighlights([]);
    }
  }, [activeIndex]);

  const deleteEvenItem = (eventIndex) => {
    const newEventRules = [...eventRules];
    const eventList = newEventRules[0];
    eventList.splice(eventIndex, 1);

    if (newEventRules[0].length === 0) {
      // if delete the last event, keep one empty event on UI
      const defaultEventRule = getEmptyRuleGroupInAPIForm();
      eventList.push(defaultEventRule);
    }
    // if delete the last event, set the activeIndex to the last event
    setActiveIndex(eventIndex === 0 ? 0 : eventIndex - 1);
    setEventRules(newEventRules);
  };

  const addEventItem = () => {
    const newEvent = getEmptyRuleGroupInAPIForm();
    const newEventRules = [...eventRules];
    newEventRules[0].push(newEvent);
    setActiveIndex(newEventRules.length - 1);
    setEventRules(newEventRules);
  };
  return (
    <div className={styles.mappingWrapper} key={activeIndex}>
      {eventRules?.[0]?.map((event, eventOrIndex) => {
        return (
          <EventBox
            errorFields={validation?.fields}
            errorIndexs={validation?.errorIndex?.[eventOrIndex]}
            onClick={() => {
              setActiveIndex(eventOrIndex);
            }}
            onDelete={() => {
              deleteEvenItem(eventOrIndex);
            }}
            key={`EventBox_key_${eventOrIndex}`}
            event={event}
            index={eventOrIndex}
            isHighlight={highlights.includes(eventOrIndex)}
            isActive={activeIndex === eventOrIndex}
            onChange={(event) => {
              eventRules[0][eventOrIndex] = event;
              setEventRules([...eventRules]);
            }}
            {...rest}
          />
        );
      })}
      {/*Since support clone multiple mapped events add new move to out of MappingRulesBuilder*/}
      {disableAddRawEvent ? null : (
        <div className={styles.addNewEvent} onClick={() => addEventItem()}>
          <Icon component={PlusIcon} style={{ color: '#0046FF' }} />
          &nbsp;Add Raw Event
        </div>
      )}
    </div>
  );
};

export default React.memo(MappingRulesBuilder, isEqual);
